import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { Check, ChevronDown } from 'lucide-react';
import { useState } from 'react';

export interface OpcionMultiSelect {
  value: string;
  label: string;
}

interface Props {
  opciones: OpcionMultiSelect[];
  seleccionados: string[];
  onChange: (valores: string[]) => void;
  /** Texto del botón cuando no hay nada seleccionado. */
  placeholder: string;
  /** Texto singular para el resumen ("planta" → "2 plantas"). */
  etiquetaResumen?: string;
  className?: string;
  disabled?: boolean;
}

/**
 * Dropdown de selección múltiple con checkboxes (Popover + lista), para
 * filtros tipo "Estado"/"Planta" donde el usuario quiere combinar varios
 * valores a la vez (antes eran `Select` de un solo valor). No hay
 * componente shadcn de multi-select en el proyecto — este es el genérico
 * reusable.
 *
 * Sin selección = "todos" (el caller interpreta `[]` como sin filtro).
 */
export function MultiSelect({
  opciones,
  seleccionados,
  onChange,
  placeholder,
  etiquetaResumen,
  className,
  disabled,
}: Props) {
  const [abierto, setAbierto] = useState(false);

  const toggle = (valor: string) => {
    if (seleccionados.includes(valor)) {
      onChange(seleccionados.filter((v) => v !== valor));
    } else {
      onChange([...seleccionados, valor]);
    }
  };

  const resumen =
    seleccionados.length === 0
      ? placeholder
      : seleccionados.length === 1
        ? (opciones.find((o) => o.value === seleccionados[0])?.label ?? seleccionados[0])
        : `${seleccionados.length} ${etiquetaResumen ?? 'seleccionados'}`;

  return (
    <Popover open={abierto} onOpenChange={setAbierto}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            'h-9 w-full justify-between gap-1.5 font-normal',
            seleccionados.length === 0 && 'text-muted-foreground',
            className,
          )}
        >
          <span className="truncate">{resumen}</span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-1" align="start">
        {seleccionados.length > 0 && (
          <button
            type="button"
            onClick={() => onChange([])}
            className="mb-1 w-full rounded-sm px-2 py-1.5 text-left text-xs text-muted-foreground hover:bg-zinc-100"
          >
            Limpiar selección
          </button>
        )}
        <div className="max-h-64 space-y-0.5 overflow-y-auto">
          {opciones.map((o) => {
            const activo = seleccionados.includes(o.value);
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => toggle(o.value)}
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-zinc-100"
              >
                <span
                  className={cn(
                    'flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border',
                    activo ? 'border-primary bg-primary text-primary-foreground' : 'border-input',
                  )}
                >
                  {activo && <Check className="h-3 w-3" />}
                </span>
                <span className="truncate">{o.label}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
